import { cva } from 'class-variance-authority';

import { cn } from '@heidi/utils';
import { focusRing } from '../styles/focus-ring';
import { typographyVariant } from '../typography';

const itemVariants = cva(
  cn(
    typographyVariant({ variant: 'p2' }),
    'group/item flex w-full flex-wrap items-center rounded-lg border outline-none transition-colors duration-100',
    focusRing,
    '[&:is(a,button)]:cursor-pointer [&:is(a,button)]:hover:bg-surface-hover',
    // Keeps an Item-as-trigger lit while its associated surface is open, so the
    // trigger ↔ surface pairing reads visually. Radix triggers set
    // `aria-expanded="true"` on the trigger element while open.
    '[&:is(a,button)]:aria-expanded:bg-surface-hover'
  ),
  {
    variants: {
      variant: {
        default: 'border-transparent',
        outline: 'border-border',
        muted: 'border-transparent bg-background-tertiary',
      },
      size: {
        default: 'gap-2.5 px-3 py-2.5',
        // `min-h-8` pins single-line rows to a 32px effective height, matching
        // Button `size="sm"` (`h-8`). `py-1` keeps single-line rows at exactly 32px
        // (4px < the slack above the 21px line box) while still giving breathing
        // room when an `ItemDescription` makes the row wrap to two lines and grow.
        // Inside a dropdown menu the host item owns spacing, so neutralise both.
        sm: 'min-h-8 gap-2 px-2.5 py-1 [[data-slot=dropdown-menu-content]_&]:min-h-0 [[data-slot=dropdown-menu-content]_&]:p-0',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

const itemMediaVariants = cva(
  cn(
    'flex shrink-0 items-center justify-center gap-2',
    'group-has-[[data-slot=item-description]]/item:translate-y-0.5 group-has-[[data-slot=item-description]]/item:self-start',
    '[&_svg]:pointer-events-none'
  ),
  {
    variants: {
      variant: {
        // `w-4` reserves the icon-column width so mixed media (icons, dots, avatars)
        // share the same label-start x-position. Larger children (e.g. a 24px Avatar)
        // overflow symmetrically via `justify-center` so their visual center stays on
        // the icon column's center and the overflow bleeds into the row's padding.
        default: 'w-4 bg-transparent',
        icon: "w-4 [&_svg:not([class*='size-'])]:size-4",
        image: cn(
          'size-10 overflow-hidden rounded-sm',
          'group-data-[size=sm]/item:size-6',
          '[&_img]:size-full [&_img]:object-cover'
        ),
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export { itemMediaVariants, itemVariants };
